<template>
  <div class="pdf-viewer" ref="viewerContainer">
    <!-- Toolbar -->
    <div class="pdf-toolbar" v-if="showToolbar">
      <div class="pdf-toolbar__left">
        <span class="pdf-toolbar__page-info">
          Página {{ currentPage }} de {{ totalPages }}
        </span>
      </div>
      <div class="pdf-toolbar__center">
        <button
          class="pdf-toolbar__btn"
          @click="prevPage"
          :disabled="currentPage <= 1"
        >
          <i class="el-icon-arrow-left"></i>
        </button>
        <button
          class="pdf-toolbar__btn"
          @click="nextPage"
          :disabled="currentPage >= totalPages"
        >
          <i class="el-icon-arrow-right"></i>
        </button>
        <button
          class="pdf-toolbar__btn"
          @click="zoomOut"
          :disabled="scale <= 0.5"
        >
          <i class="el-icon-zoom-out"></i>
        </button>
        <span class="pdf-toolbar__zoom">{{ Math.round(scale * 100) }}%</span>
        <button class="pdf-toolbar__btn" @click="zoomIn" :disabled="scale >= 3">
          <i class="el-icon-zoom-in"></i>
        </button>
      </div>
      <div class="pdf-toolbar__right">
        <slot name="toolbar-right"></slot>
      </div>
    </div>

    <!-- PDF Canvas Area -->
    <div class="pdf-canvas-area" ref="canvasArea" @scroll="handleScroll">
      <div
        class="pdf-canvas-wrapper"
        v-if="loaded"
        :style="wrapperStyle"
        :class="{ 'pdf-canvas-wrapper--editable': editable && signatureZone }"
        @click.self="placeZoneOnClick"
      >
        <canvas ref="pdfCanvas" @click="placeZoneOnClick"></canvas>

        <!-- Draggable Signature Zone (only on the page where it was placed) -->
        <div
          v-if="signatureZone && editable && zonePageFixed === currentPage"
          class="signature-zone"
          :class="{ 'signature-zone--dragging': isDragging }"
          :style="zoneStyle"
          @mousedown.prevent="startDrag"
          @touchstart.prevent="startDragTouch"
        >
          <div class="signature-zone__label">
            <i class="el-icon-edit"></i>
            {{ signatureZone.label || "Tu firma aquí" }}
          </div>
          <!-- Resize handle -->
          <div
            class="signature-zone__resize"
            @mousedown.stop.prevent="startResize"
            @touchstart.stop.prevent="startResizeTouch"
          ></div>
        </div>

        <!-- Read-only signature zones (for display — only on matching page) -->
        <div
          v-for="(zone, idx) in currentPageZones"
          :key="'zone-' + idx"
          class="signature-zone"
          :class="
            zone.signed ? 'signature-zone--signed' : 'signature-zone--readonly'
          "
          :style="getZoneStyle(zone)"
        >
          <div v-if="zone.signed" class="signature-zone__stamp">
            <div class="signature-zone__stamp-header">
              Firmado electrónicamente por:
            </div>
            <div class="signature-zone__stamp-name">
              {{
                zone.label
                  ? zone.label.replace("✓ Firmado por: ", "")
                  : "Firmante"
              }}
            </div>
            <div class="signature-zone__stamp-footer">
              Verificar en app.mydessk.com
            </div>
          </div>
          <div v-else class="signature-zone__label">
            <i class="el-icon-edit"></i>
            {{ zone.label || "Firma" }}
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="pdf-loading">
        <i class="el-icon-loading"></i>
        <span>Cargando PDF...</span>
      </div>

      <!-- Error -->
      <div v-if="error" class="pdf-error">
        <i class="el-icon-warning-outline"></i>
        <span>{{ error }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PdfViewer",
  props: {
    // PDF source — either ArrayBuffer or URL
    pdfData: { type: [ArrayBuffer, Uint8Array, String], default: null },
    // Show toolbar
    showToolbar: { type: Boolean, default: true },
    // Editable signature zone (single, draggable)
    signatureZone: { type: Object, default: null },
    // Read-only zones to display
    displayZones: { type: Array, default: () => [] },
    // Allow editing/dragging the zone
    editable: { type: Boolean, default: false },
    // Initial page
    initialPage: { type: Number, default: 1 },
    // Initial scale
    initialScale: { type: Number, default: 1.0 }
  },
  data() {
    return {
      pdfDoc: null,
      currentPage: this.initialPage,
      totalPages: 0,
      scale: this.initialScale,
      loaded: false,
      loading: false,
      error: null,
      canvasWidth: 0,
      canvasHeight: 0,
      // Drag state
      isDragging: false,
      isResizing: false,
      dragStart: { x: 0, y: 0 },
      zonePos: { x: 50, y: 70, w: 25, h: 8 }, // percentages
      zonePageFixed: this.initialPage, // The page where the zone is stamped
      pdfjsLib: null
    };
  },
  computed: {
    wrapperStyle() {
      return {
        width: this.canvasWidth + "px",
        height: this.canvasHeight + "px",
        position: "relative"
      };
    },
    zoneStyle() {
      return {
        left: this.zonePos.x + "%",
        top: this.zonePos.y + "%",
        width: this.zonePos.w + "%",
        height: this.zonePos.h + "%"
      };
    },
    /** Filter displayZones to only show zones on the current page */
    currentPageZones() {
      if (!this.displayZones || !this.displayZones.length) return [];
      return this.displayZones.filter(
        zone => (zone.page || 1) === this.currentPage
      );
    }
  },
  watch: {
    pdfData: {
      handler(val) {
        if (val) this.loadPdf();
      },
      immediate: true
    },
    signatureZone: {
      handler(val) {
        if (val) {
          this.zonePos = {
            x: val.x ?? 50,
            y: val.y ?? 70,
            w: val.w ?? 25,
            h: val.h ?? 8
          };
          if (val.page) {
            this.zonePageFixed = val.page;
            this.currentPage = val.page;
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    async loadPdf() {
      this.loading = true;
      this.error = null;
      this.loaded = false;

      try {
        await this.loadPdfjsLib();

        let source;
        if (typeof this.pdfData === "string") {
          source = this.pdfData;
        } else {
          // Copy the buffer — pdf.js transfers/consumes the original
          const copy = new Uint8Array(this.pdfData);
          source = { data: copy };
        }

        const loadingTask = this.pdfjsLib.getDocument(source);
        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;
        this.loaded = true;

        await this.$nextTick();
        await this.renderPage();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("PDF load error:", err);
        this.error = "No se pudo cargar el PDF. " + (err.message || "");
      } finally {
        this.loading = false;
      }
    },

    async loadPdfjsLib() {
      if (this.pdfjsLib) return;
      if (window.pdfjsLib) {
        this.pdfjsLib = window.pdfjsLib;
        if (!this.pdfjsLib.GlobalWorkerOptions.workerSrc) {
          this.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js";
        }
        return;
      }

      // Try primary CDN, then fallback
      const cdnUrls = [
        {
          script:
            "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.min.js",
          worker:
            "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js"
        },
        {
          script:
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js",
          worker:
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js"
        }
      ];

      for (const cdn of cdnUrls) {
        try {
          await this._loadScript(cdn.script);
          if (window.pdfjsLib) {
            this.pdfjsLib = window.pdfjsLib;
            this.pdfjsLib.GlobalWorkerOptions.workerSrc = cdn.worker;
            return;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("PDF.js CDN failed, trying next:", cdn.script);
        }
      }

      throw new Error("No se pudo cargar la librería PDF.");
    },

    _loadScript(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Script load failed: " + src));
        document.head.appendChild(script);
      });
    },

    async renderPage() {
      if (!this.pdfDoc) return;

      const page = await this.pdfDoc.getPage(this.currentPage);
      const viewport = page.getViewport({ scale: this.scale });

      const canvas = this.$refs.pdfCanvas;
      if (!canvas) return;

      const context = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;

      canvas.width = viewport.width * dpr;
      canvas.height = viewport.height * dpr;
      canvas.style.width = viewport.width + "px";
      canvas.style.height = viewport.height + "px";

      this.canvasWidth = viewport.width;
      this.canvasHeight = viewport.height;

      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      await page.render({ canvasContext: context, viewport }).promise;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.renderPage();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.renderPage();
      }
    },

    zoomIn() {
      this.scale = Math.min(3, this.scale + 0.2);
      this.renderPage();
    },

    zoomOut() {
      this.scale = Math.max(0.5, this.scale - 0.2);
      this.renderPage();
    },

    handleScroll() {},

    // ─── Click-to-place ──────────────────────────────────────────────
    placeZoneOnClick(e) {
      if (!this.editable || !this.signatureZone) return;
      // Ignore if we just finished dragging/resizing
      if (this.isDragging || this.isResizing || this._justDragged) return;

      const wrapper = this.$el.querySelector(".pdf-canvas-wrapper");
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Convert to percentage, centering the zone on the click point
      const halfW = this.zonePos.w / 2;
      const halfH = this.zonePos.h / 2;
      const xPct = Math.max(
        0,
        Math.min(
          100 - this.zonePos.w,
          (clickX / this.canvasWidth) * 100 - halfW
        )
      );
      const yPct = Math.max(
        0,
        Math.min(
          100 - this.zonePos.h,
          (clickY / this.canvasHeight) * 100 - halfH
        )
      );

      this.zonePos.x = Math.round(xPct * 100) / 100;
      this.zonePos.y = Math.round(yPct * 100) / 100;

      // Fix zone to current page
      this.zonePageFixed = this.currentPage;
      this.emitZoneUpdate();
    },

    // ─── Drag Logic ─────────────────────────────────────────────────
    startDrag(e) {
      this.isDragging = true;
      this.dragStart = { x: e.clientX, y: e.clientY };
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },

    startDragTouch(e) {
      if (!e.touches.length) return;
      this.isDragging = true;
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      document.addEventListener("touchmove", this.onDragTouch, {
        passive: false
      });
      document.addEventListener("touchend", this.stopDrag);
    },

    onDrag(e) {
      if (!this.isDragging) return;
      this.applyDragDelta(
        e.clientX - this.dragStart.x,
        e.clientY - this.dragStart.y
      );
      this.dragStart = { x: e.clientX, y: e.clientY };
    },

    onDragTouch(e) {
      if (!this.isDragging || !e.touches.length) return;
      e.preventDefault();
      this.applyDragDelta(
        e.touches[0].clientX - this.dragStart.x,
        e.touches[0].clientY - this.dragStart.y
      );
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    },

    applyDragDelta(dx, dy) {
      const pctX = (dx / this.canvasWidth) * 100;
      const pctY = (dy / this.canvasHeight) * 100;
      this.zonePos.x = Math.max(
        0,
        Math.min(100 - this.zonePos.w, this.zonePos.x + pctX)
      );
      this.zonePos.y = Math.max(
        0,
        Math.min(100 - this.zonePos.h, this.zonePos.y + pctY)
      );
    },

    stopDrag() {
      const wasDragging = this.isDragging || this.isResizing;
      this.isDragging = false;
      this.isResizing = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchmove", this.onDragTouch);
      document.removeEventListener("touchmove", this.onResizeTouch);
      document.removeEventListener("touchend", this.stopDrag);
      // Fix the zone to the current page when user drags/resizes
      this.zonePageFixed = this.currentPage;
      this.emitZoneUpdate();
      // Prevent the mouseup from triggering a click-to-place
      if (wasDragging) {
        this._justDragged = true;
        setTimeout(() => {
          this._justDragged = false;
        }, 200);
      }
    },

    // ─── Resize Logic ───────────────────────────────────────────────
    startResize(e) {
      this.isResizing = true;
      this.dragStart = { x: e.clientX, y: e.clientY };
      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopDrag);
    },

    startResizeTouch(e) {
      if (!e.touches.length) return;
      this.isResizing = true;
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      document.addEventListener("touchmove", this.onResizeTouch, {
        passive: false
      });
      document.addEventListener("touchend", this.stopDrag);
    },

    onResize(e) {
      if (!this.isResizing) return;
      const dx = e.clientX - this.dragStart.x;
      const dy = e.clientY - this.dragStart.y;
      const pctX = (dx / this.canvasWidth) * 100;
      const pctY = (dy / this.canvasHeight) * 100;
      this.zonePos.w = Math.max(10, Math.min(60, this.zonePos.w + pctX));
      this.zonePos.h = Math.max(4, Math.min(20, this.zonePos.h + pctY));
      this.dragStart = { x: e.clientX, y: e.clientY };
    },

    onResizeTouch(e) {
      if (!this.isResizing || !e.touches.length) return;
      e.preventDefault();
      const dx = e.touches[0].clientX - this.dragStart.x;
      const dy = e.touches[0].clientY - this.dragStart.y;
      const pctX = (dx / this.canvasWidth) * 100;
      const pctY = (dy / this.canvasHeight) * 100;
      this.zonePos.w = Math.max(10, Math.min(60, this.zonePos.w + pctX));
      this.zonePos.h = Math.max(4, Math.min(20, this.zonePos.h + pctY));
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    },

    emitZoneUpdate() {
      if (!this.editable || !this.signatureZone) return;
      // Emit using the zone's fixed page (the page where it was placed/dragged)
      this.$emit("zone-update", {
        page: this.zonePageFixed,
        x: Math.round(this.zonePos.x * 100) / 100,
        y: Math.round(this.zonePos.y * 100) / 100,
        w: Math.round(this.zonePos.w * 100) / 100,
        h: Math.round(this.zonePos.h * 100) / 100
      });
    },

    getZoneStyle(zone) {
      return {
        left: (zone.x || 0) + "%",
        top: (zone.y || 0) + "%",
        width: (zone.width || zone.w || 25) + "%",
        height: (zone.height || zone.h || 8) + "%"
      };
    },

    /** Public method: get current zone data */
    getZoneData() {
      return {
        page: this.zonePageFixed,
        x: Math.round(this.zonePos.x * 100) / 100,
        y: Math.round(this.zonePos.y * 100) / 100,
        w: Math.round(this.zonePos.w * 100) / 100,
        h: Math.round(this.zonePos.h * 100) / 100
      };
    }
  },
  beforeDestroy() {
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mousemove", this.onResize);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("touchmove", this.onDragTouch);
    document.removeEventListener("touchmove", this.onResizeTouch);
    document.removeEventListener("touchend", this.stopDrag);
  }
};
</script>

<style scoped>
.pdf-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f0f2f5;
  border-radius: 12px;
  overflow: hidden;
}

.pdf-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid #e4eaf6;
  flex-shrink: 0;
}

.pdf-toolbar__page-info {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.pdf-toolbar__center {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pdf-toolbar__btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e4eaf6;
  border-radius: 8px;
  background: #fff;
  color: #31355c;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.pdf-toolbar__btn:hover:not(:disabled) {
  background: #f7f8f9;
  border-color: #3fdbbf;
}

.pdf-toolbar__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pdf-toolbar__zoom {
  font-size: 12px;
  color: #989aae;
  min-width: 40px;
  text-align: center;
}

.pdf-canvas-area {
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  padding: 20px;
}

.pdf-canvas-wrapper {
  position: relative;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  background: #fff;
}

.pdf-canvas-wrapper canvas {
  display: block;
}

.pdf-canvas-wrapper--editable {
  cursor: crosshair;
}

.pdf-canvas-wrapper--editable canvas {
  cursor: crosshair;
}

/* Signature Zone */
.signature-zone {
  position: absolute;
  border: 2px dashed #3fdbbf;
  background: rgba(63, 219, 191, 0.08);
  border-radius: 8px;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;
  z-index: 10;
}

.signature-zone:hover {
  background: rgba(63, 219, 191, 0.15);
  border-color: #2bc4a8;
}

.signature-zone--dragging {
  cursor: grabbing;
  border-color: #31355c;
  background: rgba(49, 53, 92, 0.1);
}

.signature-zone--readonly {
  cursor: default;
  border-color: #989aae;
  background: rgba(152, 154, 174, 0.08);
}

.signature-zone__label {
  font-size: 11px;
  color: #31355c;
  font-weight: 600;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 8px;
}

.signature-zone__label i {
  margin-right: 4px;
}

.signature-zone__resize {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 12px;
  height: 12px;
  background: #3fdbbf;
  border-radius: 3px;
  cursor: nwse-resize;
}

/* Loading & Error */
.pdf-loading,
.pdf-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: #989aae;
  font-size: 14px;
}

.pdf-loading i,
.pdf-error i {
  font-size: 32px;
}

.pdf-error {
  color: #e74c3c;
}

/* Signed stamp zone */
.signature-zone--signed {
  cursor: default;
  border: 2px solid #1a1a1a;
  background: #fff;
  border-radius: 4px;
  flex-direction: row;
  align-items: stretch;
  padding: 0;
  overflow: hidden;
}

.signature-zone--signed:hover {
  background: #fff;
  border-color: #1a1a1a;
}

.signature-zone__stamp {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  padding: 4px 8px;
  pointer-events: none;
  background: #fff;
  border-left: 4px solid #1a1a1a;
}

.signature-zone__stamp-header {
  font-size: 7px;
  color: #555;
  line-height: 1.2;
  white-space: nowrap;
}

.signature-zone__stamp-name {
  font-size: 10px;
  font-weight: 900;
  color: #1a1a1a;
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.signature-zone__stamp-footer {
  font-size: 6px;
  color: #777;
  line-height: 1.2;
  white-space: nowrap;
}
</style>
